<template>
  <div class="account-page">
    <UserAppChrome back-mode="history" />

    <main class="account-shell">
      <UserSectionHeader
        eyebrow="Il tuo profilo"
        title="Account"
        description="Mantieni aggiornati i tuoi dati personali per ricevere un’assistenza sempre più precisa."
      />

      <section class="account-summary" aria-label="Riepilogo profilo">
        <span class="account-summary__avatar" aria-hidden="true">{{ profileInitials }}</span>
        <div class="account-summary__copy">
          <small>PROFILO PERSONALE</small>
          <strong>{{ profileName }}</strong>
          <span>{{ form.email || 'Email non disponibile' }}</span>
        </div>
        <span class="account-summary__status">
          <span aria-hidden="true"></span>
          Account attivo
        </span>
      </section>

      <section class="profile-card" aria-labelledby="profile-card-title">
        <header class="profile-card__header">
          <span class="profile-card__icon" aria-hidden="true">
            <span class="material-icons">person_outline</span>
          </span>
          <div>
            <p>Dati principali</p>
            <h2 id="profile-card-title">Informazioni personali</h2>
          </div>
          <span class="profile-card__badge">Profilo</span>
        </header>

        <div v-if="loading" class="loading-card" role="status" aria-live="polite">
          <span class="loading-card__spinner" aria-hidden="true"></span>
          <strong>Caricamento dati</strong>
          <p>Stiamo recuperando le informazioni del tuo account.</p>
        </div>

        <form v-else class="form-grid" @submit.prevent="saveProfile">
            <div class="field">
              <label for="name">
                <span class="material-icons" aria-hidden="true">badge</span>
                Nome
              </label>
              <input id="name" v-model.trim="form.name" type="text" autocomplete="given-name" required />
            </div>

            <div class="field">
              <label for="surname">
                <span class="material-icons" aria-hidden="true">badge</span>
                Cognome
              </label>
              <input id="surname" v-model.trim="form.surname" type="text" autocomplete="family-name" required />
            </div>

            <div class="field">
              <label for="phone">
                <span class="material-icons" aria-hidden="true">phone</span>
                Telefono
              </label>
              <input
                id="phone"
                v-model.trim="form.phone"
                type="tel"
                inputmode="tel"
                autocomplete="tel"
                placeholder="Es. +39..."
                pattern="[+0-9 ()-]{8,24}"
                required
              />
            </div>

            <div class="field">
              <label>
                <span class="material-icons" aria-hidden="true">cake</span>
                Data di nascita
              </label>
              <div class="birthdate-group">
                <select v-model="birthdateParts.day" aria-label="Giorno di nascita">
                  <option value="">GG</option>
                  <option v-for="opt in DAY_OPTIONS" :key="opt.value" :value="opt.value">
                    {{ opt.label }}
                  </option>
                </select>
                <select v-model="birthdateParts.month" aria-label="Mese di nascita">
                  <option value="">MM</option>
                  <option v-for="opt in MONTH_OPTIONS" :key="opt.value" :value="opt.value">
                    {{ opt.label }}
                  </option>
                </select>
                <select v-model="birthdateParts.year" aria-label="Anno di nascita">
                  <option value="">AAAA</option>
                  <option v-for="opt in YEAR_OPTIONS" :key="opt" :value="opt">
                    {{ opt }}
                  </option>
                </select>
              </div>
            </div>

            <div class="field">
              <label for="regione">
                <span class="material-icons" aria-hidden="true">map</span>
                Regione
              </label>
              <input id="regione" v-model.trim="form.regione" type="text" autocomplete="address-level1" />
            </div>

            <div class="field">
              <label for="indirizzo">
                <span class="material-icons" aria-hidden="true">home</span>
                Indirizzo
              </label>
              <input id="indirizzo" v-model.trim="form.indirizzo" type="text" autocomplete="street-address" />
            </div>

            <div class="field full">
              <label for="email">
                <span class="material-icons" aria-hidden="true">mail_outline</span>
                Email
              </label>
              <input id="email" :value="form.email" type="email" autocomplete="email" disabled />
              <small class="hint">
                <span class="material-icons" aria-hidden="true">lock</span>
                Per modificare l’email contatta l’assistenza.
              </small>
            </div>

            <div class="field full">
              <label for="codFiscale">
                <span class="material-icons" aria-hidden="true">fingerprint</span>
                Codice fiscale
              </label>
              <input
                id="codFiscale"
                v-model.trim="form.codFiscale"
                type="text"
                autocomplete="off"
                maxlength="16"
              />
            </div>

            <footer class="form-actions full">
              <div class="form-actions__copy">
                <span class="material-icons" aria-hidden="true">shield</span>
                <p>I tuoi dati vengono utilizzati esclusivamente per la gestione del percorso.</p>
              </div>
              <button class="save-button" type="submit" :disabled="saving">
                <span class="material-icons" aria-hidden="true">{{ saving ? 'hourglass_top' : 'save' }}</span>
                <span>{{ saving ? 'Salvataggio…' : 'Salva modifiche' }}</span>
              </button>
            </footer>

            <p v-if="successMessage" class="message success" role="status">
              <span class="material-icons" aria-hidden="true">check_circle</span>
              {{ successMessage }}
            </p>
            <p v-if="errorMessage" class="message error" role="alert">
              <span class="material-icons" aria-hidden="true">error_outline</span>
              {{ errorMessage }}
            </p>
          </form>
      </section>

      <aside class="account-note">
        <span class="material-icons" aria-hidden="true">support_agent</span>
        <div>
          <strong>Hai bisogno di modificare l’email?</strong>
          <p>Per proteggere il tuo account, questa variazione viene gestita dal nostro team di assistenza.</p>
        </div>
      </aside>

      <section class="account-management" aria-labelledby="account-management-title">
        <header class="account-management__header">
          <div>
            <p>Impostazioni</p>
            <h2 id="account-management-title">Gestione account</h2>
          </div>
          <span class="material-icons" aria-hidden="true">manage_accounts</span>
        </header>

        <div class="account-action-list">
          <button
            type="button"
            class="account-action"
            :aria-expanded="showContacts"
            @click="toggleContacts"
          >
            <span class="account-action__icon material-icons" aria-hidden="true">contact_support</span>
            <span class="account-action__copy">
              <strong>Contatti</strong>
              <small>WhatsApp, telefono ed email del team</small>
            </span>
            <span class="material-icons" aria-hidden="true">{{ showContacts ? 'expand_less' : 'chevron_right' }}</span>
          </button>

          <div v-if="showContacts" class="account-panel account-contacts">
            <a href="https://wa.me/393279136921" target="_blank" rel="noopener noreferrer">
              <span class="material-icons" aria-hidden="true">chat</span>
              <span><small>WhatsApp</small><strong>+39 327 913 6921</strong></span>
              <span class="material-icons" aria-hidden="true">north_east</span>
            </a>
            <a href="tel:+393279136921">
              <span class="material-icons" aria-hidden="true">call</span>
              <span><small>Telefono</small><strong>+39 327 913 6921</strong></span>
              <span class="material-icons" aria-hidden="true">arrow_forward</span>
            </a>
            <a href="mailto:info@fusco.fit">
              <span class="material-icons" aria-hidden="true">mail_outline</span>
              <span><small>Email</small><strong>info@fusco.fit</strong></span>
              <span class="material-icons" aria-hidden="true">arrow_forward</span>
            </a>
          </div>

          <button
            type="button"
            class="account-action"
            :disabled="isLoggingOut"
            @click="logout"
          >
            <span class="account-action__icon material-icons" aria-hidden="true">logout</span>
            <span class="account-action__copy">
              <strong>{{ isLoggingOut ? 'Uscita in corso…' : 'Logout' }}</strong>
              <small>Esci in sicurezza da questo account</small>
            </span>
            <span class="material-icons" aria-hidden="true">arrow_forward</span>
          </button>

          <button
            type="button"
            class="account-action account-action--danger"
            :aria-expanded="showDeletionRequest"
            @click="toggleDeletionRequest"
          >
            <span class="account-action__icon material-icons" aria-hidden="true">person_remove</span>
            <span class="account-action__copy">
              <strong>Richiedi cancellazione</strong>
              <small>Invia una richiesta per eliminare il profilo</small>
            </span>
            <span class="material-icons" aria-hidden="true">{{ showDeletionRequest ? 'expand_less' : 'chevron_right' }}</span>
          </button>

          <div v-if="showDeletionRequest" class="account-panel deletion-panel">
            <span class="material-icons" aria-hidden="true">info_outline</span>
            <div>
              <strong>La cancellazione non è immediata</strong>
              <p>Si aprirà la tua app email con una richiesta indirizzata a {{ accountDeletionEmail }}. Controlla il testo e inviala: il team verificherà la richiesta usando l’indirizzo associato al tuo account.</p>
              <a :href="deletionMailto" class="deletion-panel__button">
                <span class="material-icons" aria-hidden="true">mail</span>
                Prepara l’email di richiesta
              </a>
            </div>
          </div>

          <p v-if="accountActionError" class="account-action-error" role="alert">
            <span class="material-icons" aria-hidden="true">error_outline</span>
            {{ accountActionError }}
          </p>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { getAuth, signOut } from 'firebase/auth';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';
import UserAppChrome from './UserAppChrome.vue';
import UserSectionHeader from './UserSectionHeader.vue';
import { isValidUserPhone, normalizeUserPhone } from '@/lib/userPhone';
import { ACCOUNT_DELETION_EMAIL, buildAccountDeletionMailto } from '@/lib/accountDeletion';

const MONTH_OPTIONS = [
  { value: '01', label: '01 - Gennaio' },
  { value: '02', label: '02 - Febbraio' },
  { value: '03', label: '03 - Marzo' },
  { value: '04', label: '04 - Aprile' },
  { value: '05', label: '05 - Maggio' },
  { value: '06', label: '06 - Giugno' },
  { value: '07', label: '07 - Luglio' },
  { value: '08', label: '08 - Agosto' },
  { value: '09', label: '09 - Settembre' },
  { value: '10', label: '10 - Ottobre' },
  { value: '11', label: '11 - Novembre' },
  { value: '12', label: '12 - Dicembre' },
];

const DAY_OPTIONS = Array.from({ length: 31 }, (_, idx) => {
  const value = String(idx + 1).padStart(2, '0');
  return { value, label: value };
});

const buildYearOptions = () => {
  const now = new Date();
  const maxYear = now.getFullYear() - 10;
  const minYear = now.getFullYear() - 90;
  const list = [];
  for (let year = maxYear; year >= minYear; year -= 1) {
    list.push(String(year));
  }
  return list;
};

const YEAR_OPTIONS = buildYearOptions();

export default {
  name: 'UserAnagrafica',
  components: { UserAppChrome, UserSectionHeader },
  setup() {
    const router = useRouter();
    const store = useStore();
    const auth = getAuth();
    const db = getFirestore();

    const form = ref({
      name: '',
      surname: '',
      phone: '',
      email: '',
      codFiscale: '',
      nascita: '',
      regione: '',
      indirizzo: '',
    });
    const birthdateParts = reactive({ day: '', month: '', year: '' });
    const loading = ref(true);
    const saving = ref(false);
    const successMessage = ref('');
    const errorMessage = ref('');
    const showContacts = ref(false);
    const showDeletionRequest = ref(false);
    const isLoggingOut = ref(false);
    const accountActionError = ref('');

    const profileName = computed(() => {
      const fullName = [form.value.name, form.value.surname].filter(Boolean).join(' ').trim();
      return fullName || 'Il tuo account';
    });

    const profileInitials = computed(() => {
      const initials = [form.value.name, form.value.surname]
        .filter(Boolean)
        .map((value) => String(value).trim().charAt(0).toUpperCase())
        .join('');
      return initials || 'FF';
    });

    const deletionMailto = computed(() => {
      const accountEmail = form.value.email || auth.currentUser?.email || '';
      return buildAccountDeletionMailto(accountEmail);
    });

    const toggleContacts = () => {
      showContacts.value = !showContacts.value;
      if (showContacts.value) showDeletionRequest.value = false;
    };

    const toggleDeletionRequest = () => {
      showDeletionRequest.value = !showDeletionRequest.value;
      if (showDeletionRequest.value) showContacts.value = false;
    };

    const logout = async () => {
      if (isLoggingOut.value) return;
      isLoggingOut.value = true;
      accountActionError.value = '';

      try {
        await signOut(auth);
        store.commit('CLEAR_USER');
        store.commit('SET_USER_TOKEN', null);
        store.commit('SET_USER_EMAIL', null);
        localStorage.removeItem('userEmail');
        await router.replace('/');
      } catch (error) {
        console.error('Errore durante il logout', error);
        accountActionError.value = 'Non siamo riusciti a effettuare il logout. Riprova.';
      } finally {
        isLoggingOut.value = false;
      }
    };

    const parseBirthdateParts = (value) => {
      if (!value) return null;
      const str = String(value).trim();
      const iso = str.match(/^(\d{4})[-/](\d{2})[-/](\d{2})$/);
      if (iso) {
        return { year: iso[1], month: iso[2], day: iso[3] };
      }
      const dmy = str.match(/^(\d{2})[-/](\d{2})[-/](\d{4})$/);
      if (dmy) {
        return { year: dmy[3], month: dmy[2], day: dmy[1] };
      }
      const parsed = new Date(str);
      if (Number.isNaN(parsed.getTime())) return null;
      return {
        year: String(parsed.getUTCFullYear()),
        month: String(parsed.getUTCMonth() + 1).padStart(2, '0'),
        day: String(parsed.getUTCDate()).padStart(2, '0'),
      };
    };

    const syncBirthdatePartsFromValue = (value) => {
      const parsed = parseBirthdateParts(value);
      birthdateParts.year = parsed?.year || '';
      birthdateParts.month = parsed?.month || '';
      birthdateParts.day = parsed?.day || '';
    };

    const buildBirthdateValue = () => {
      if (birthdateParts.year && birthdateParts.month && birthdateParts.day) {
        return `${birthdateParts.year}-${birthdateParts.month}-${birthdateParts.day}`;
      }
      return '';
    };

    const loadProfile = async () => {
      loading.value = true;
      errorMessage.value = '';
      successMessage.value = '';

      const currentUser = auth.currentUser;
      if (!currentUser) {
        errorMessage.value = 'Sessione scaduta. Accedi di nuovo.';
        loading.value = false;
        return;
      }

      try {
        const docRef = doc(db, 'users', currentUser.uid);
        const snapshot = await getDoc(docRef);
        if (snapshot.exists()) {
          const data = snapshot.data();
          form.value = {
            name: data.name || '',
            surname: data.surname || '',
            phone: data.phone || data.telefono || '',
            email: data.email || currentUser.email || '',
            codFiscale: data.codFiscale || '',
            nascita: data.nascita || '',
            regione: data.regione || '',
            indirizzo: data.indirizzo || '',
          };
          syncBirthdatePartsFromValue(data.nascita || '');
        } else {
          errorMessage.value = 'Profilo utente non trovato.';
        }
      } catch (error) {
        console.error('Errore nel caricamento del profilo', error);
        errorMessage.value = 'Errore nel caricamento del profilo.';
      } finally {
        loading.value = false;
      }
    };

    const saveProfile = async () => {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        errorMessage.value = 'Sessione scaduta. Accedi di nuovo.';
        return;
      }

      saving.value = true;
      successMessage.value = '';
      errorMessage.value = '';

      try {
        const normalizedPhone = normalizeUserPhone(form.value.phone);
        if (!isValidUserPhone(normalizedPhone)) {
          errorMessage.value = 'Inserisci un numero di telefono valido.';
          return;
        }

        const docRef = doc(db, 'users', currentUser.uid);
        await updateDoc(docRef, {
          name: form.value.name,
          surname: form.value.surname,
          phone: normalizedPhone,
          codFiscale: form.value.codFiscale ? form.value.codFiscale.toUpperCase() : '',
          nascita: form.value.nascita,
          regione: form.value.regione,
          indirizzo: form.value.indirizzo,
        });
        form.value.phone = normalizedPhone;
        successMessage.value = 'Dati aggiornati correttamente.';
      } catch (error) {
        console.error('Errore nel salvataggio del profilo', error);
        errorMessage.value = 'Non siamo riusciti a salvare le modifiche. Riprova.';
      } finally {
        saving.value = false;
      }
    };

    watch(
      () => [birthdateParts.day, birthdateParts.month, birthdateParts.year],
      () => {
        form.value.nascita = buildBirthdateValue();
      },
      { immediate: true }
    );

    onMounted(loadProfile);

    return {
      form,
      loading,
      saving,
      successMessage,
      errorMessage,
      profileName,
      profileInitials,
      deletionMailto,
      accountDeletionEmail: ACCOUNT_DELETION_EMAIL,
      showContacts,
      showDeletionRequest,
      isLoggingOut,
      accountActionError,
      toggleContacts,
      toggleDeletionRequest,
      logout,
      saveProfile,
      birthdateParts,
      DAY_OPTIONS,
      MONTH_OPTIONS,
      YEAR_OPTIONS,
    };
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Montserrat:wght@700;900&display=swap');

.account-page {
  --ff-bg: #f5f4f0;
  --ff-white: #fff;
  --ff-black: #111110;
  --ff-muted: #66635f;
  --ff-red: #c8102e;
  --ff-red-dark: #990b22;
  position: relative;
  isolation: isolate;
  min-height: 100vh;
  min-height: 100dvh;
  overflow-x: hidden;
  color: var(--ff-black);
  background:
    radial-gradient(circle at 92% 8%, rgba(200, 16, 46, 0.1), transparent 25rem),
    radial-gradient(circle at 3% 74%, rgba(200, 16, 46, 0.05), transparent 28rem),
    var(--ff-bg);
  font-family: 'DM Sans', sans-serif;
  text-align: left;
}

.account-page,
.account-page *,
.account-page *::before,
.account-page *::after {
  box-sizing: border-box;
}

.account-page::before {
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;
  content: '';
  opacity: 0.24;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 180 180' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.08'/%3E%3C/svg%3E");
}

.account-page .material-icons {
  color: currentColor;
}

.account-shell {
  display: grid;
  width: min(100%, 920px);
  gap: 18px;
  margin: 0 auto;
  padding: clamp(34px, 6vw, 72px) clamp(20px, 5vw, 52px) 148px;
}

.account-summary {
  position: relative;
  display: grid;
  grid-template-columns: 64px minmax(0, 1fr) auto;
  gap: 17px;
  align-items: center;
  margin-top: -12px;
  padding: 19px 21px;
  overflow: hidden;
  color: var(--ff-white);
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 24px;
  background:
    radial-gradient(circle at 88% 0, rgba(200, 16, 46, 0.58), transparent 18rem),
    linear-gradient(140deg, #1c1c1a, #0e0e0d);
  box-shadow: 0 22px 48px rgba(17, 17, 16, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.11);
}

.account-summary::after {
  position: absolute;
  right: 22px;
  bottom: 0;
  left: 22px;
  height: 3px;
  content: '';
  border-radius: 999px 999px 0 0;
  background: linear-gradient(90deg, var(--ff-red), #ff3157, transparent 88%);
}

.account-summary__avatar {
  display: grid;
  width: 64px;
  height: 64px;
  place-items: center;
  color: var(--ff-white);
  border: 1px solid rgba(255, 255, 255, 0.17);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.1);
  font-family: 'Montserrat', sans-serif;
  font-size: 1.15rem;
  font-weight: 900;
  letter-spacing: -0.05em;
}

.account-summary__copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.account-summary__copy small {
  color: #ff4869;
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.16em;
}

.account-summary__copy strong {
  margin-top: 3px;
  overflow: hidden;
  font-size: clamp(1.08rem, 2.8vw, 1.4rem);
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.account-summary__copy span {
  margin-top: 2px;
  overflow: hidden;
  color: rgba(255, 255, 255, 0.58);
  font-size: 0.75rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.account-summary__status {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 11px;
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.72);
  font-size: 0.6875rem;
  font-weight: 700;
  white-space: nowrap;
}

.account-summary__status > span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #61c987;
  box-shadow: 0 0 0 3px rgba(97, 201, 135, 0.12);
}

.profile-card {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.94);
  border-radius: 26px;
  background: rgba(255, 255, 255, 0.72);
  box-shadow: 0 24px 58px rgba(47, 38, 32, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(22px) saturate(1.4);
  -webkit-backdrop-filter: blur(22px) saturate(1.4);
}

.profile-card::before {
  position: absolute;
  z-index: 1;
  top: 0;
  right: 8%;
  left: 8%;
  height: 3px;
  content: '';
  background: linear-gradient(90deg, transparent, var(--ff-red), transparent);
}

.profile-card__header {
  display: grid;
  grid-template-columns: 54px minmax(0, 1fr) auto;
  gap: 15px;
  align-items: center;
  padding: 22px clamp(20px, 4vw, 32px);
  border-bottom: 1px solid rgba(17, 17, 16, 0.07);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.68), rgba(255, 255, 255, 0.24));
}

.profile-card__icon {
  display: grid;
  width: 54px;
  height: 54px;
  place-items: center;
  color: var(--ff-white);
  border-radius: 17px;
  background: linear-gradient(145deg, #d4143b, var(--ff-red-dark));
  box-shadow: 0 12px 26px rgba(158, 11, 35, 0.2);
}

.profile-card__icon .material-icons {
  font-size: 24px;
}

.profile-card__header p,
.profile-card__header h2 {
  margin: 0;
}

.profile-card__header p {
  color: var(--ff-red);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.profile-card__header h2 {
  margin-top: 3px;
  color: var(--ff-black);
  font-family: 'Montserrat', sans-serif;
  font-size: clamp(1.18rem, 3vw, 1.68rem);
  font-weight: 900;
  line-height: 1;
  letter-spacing: -0.045em;
  text-align: left;
  text-transform: uppercase;
}

.profile-card__badge {
  padding: 7px 11px;
  color: var(--ff-red-dark);
  border: 1px solid rgba(200, 16, 46, 0.12);
  border-radius: 999px;
  background: rgba(200, 16, 46, 0.07);
  font-size: 0.6875rem;
  font-weight: 700;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 19px 16px;
  padding: clamp(22px, 4vw, 34px);
}

.field {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 9px;
}

.field.full,
.form-actions.full {
  grid-column: 1 / -1;
}

.field label {
  display: flex;
  align-items: center;
  gap: 7px;
  color: var(--ff-black);
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: normal;
}

.field label .material-icons {
  color: var(--ff-red);
  font-size: 17px;
}

.field input,
.birthdate-group select {
  display: block;
  width: 100%;
  min-width: 0;
  min-height: 49px;
  margin: 0;
  padding: 13px 15px;
  color: var(--ff-black);
  border: 1px solid rgba(17, 17, 16, 0.1);
  border-radius: 15px;
  background: rgba(245, 244, 240, 0.78);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
  font: inherit;
  font-size: 0.9rem;
  outline: none;
  transition: border-color 180ms ease, box-shadow 180ms ease, background 180ms ease;
}

.field input:focus,
.birthdate-group select:focus {
  border-color: rgba(200, 16, 46, 0.46);
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 0 0 4px rgba(200, 16, 46, 0.08);
}

.field input:disabled {
  color: #77736d;
  background: rgba(17, 17, 16, 0.045);
  cursor: not-allowed;
  opacity: 1;
}

.birthdate-group {
  display: grid;
  grid-template-columns: 0.72fr 1.1fr 0.9fr;
  gap: 7px;
}

.birthdate-group select {
  padding-right: 10px;
  padding-left: 10px;
  cursor: pointer;
}

.hint {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: -2px;
  color: var(--ff-muted);
  font-size: 0.6875rem;
}

.hint .material-icons {
  color: var(--ff-red);
  font-size: 14px;
}

.form-actions {
  display: flex;
  gap: 18px;
  align-items: center;
  justify-content: space-between;
  padding-top: 3px;
}

.form-actions__copy {
  display: flex;
  max-width: 360px;
  align-items: center;
  gap: 8px;
  color: var(--ff-muted);
}

.form-actions__copy .material-icons {
  flex: 0 0 auto;
  color: var(--ff-red);
  font-size: 18px;
}

.form-actions__copy p {
  margin: 0;
  font-size: 0.6875rem;
  line-height: 1.4;
}

.save-button {
  display: inline-flex;
  min-height: 47px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0;
  padding: 12px 18px;
  color: var(--ff-white);
  border: 1px solid var(--ff-red);
  border-radius: 999px;
  background: var(--ff-red);
  box-shadow: 0 12px 24px rgba(200, 16, 46, 0.2);
  font: inherit;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 180ms ease, box-shadow 180ms ease, background 180ms ease;
}

.save-button .material-icons {
  font-size: 18px;
}

.save-button:hover:not(:disabled) {
  background: var(--ff-red-dark);
  box-shadow: 0 15px 28px rgba(158, 11, 35, 0.25);
  transform: translateY(-1px);
}

.save-button:disabled {
  opacity: 0.62;
  cursor: wait;
}

.message {
  display: flex;
  grid-column: 1 / -1;
  align-items: center;
  gap: 7px;
  margin: -2px 0 0;
  padding: 11px 13px;
  border-radius: 13px;
  font-size: 0.76rem;
  font-weight: 700;
}

.message .material-icons {
  font-size: 18px;
}

.message.success {
  color: #1d6b3a;
  border: 1px solid rgba(29, 107, 58, 0.12);
  background: #e4f5e9;
}

.message.error {
  color: var(--ff-red-dark);
  border: 1px solid rgba(200, 16, 46, 0.12);
  background: rgba(200, 16, 46, 0.06);
}

.loading-card {
  display: flex;
  min-height: 260px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 32px;
  color: var(--ff-muted);
  text-align: center;
}

.loading-card__spinner {
  width: 30px;
  height: 30px;
  margin-bottom: 7px;
  border: 3px solid rgba(200, 16, 46, 0.12);
  border-top-color: var(--ff-red);
  border-radius: 50%;
  animation: account-spin 0.8s linear infinite;
}

.loading-card strong {
  color: var(--ff-black);
  font-size: 0.94rem;
}

.loading-card p {
  margin: 0;
  font-size: 0.75rem;
}

@keyframes account-spin {
  to { transform: rotate(360deg); }
}

.account-note {
  display: flex;
  gap: 13px;
  align-items: flex-start;
  padding: 17px 19px;
  color: var(--ff-muted);
  border: 1px solid rgba(255, 255, 255, 0.86);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.46);
  box-shadow: 0 12px 28px rgba(47, 38, 32, 0.06);
}

.account-note > .material-icons {
  color: var(--ff-red);
  font-size: 21px;
}

.account-note strong {
  color: var(--ff-black);
  font-size: 0.78rem;
}

.account-note p {
  margin: 3px 0 0;
  font-size: 0.72rem;
  line-height: 1.45;
}

.account-management {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.94);
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.64);
  box-shadow: 0 18px 42px rgba(47, 38, 32, 0.09), inset 0 1px 0 rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(20px) saturate(1.35);
  -webkit-backdrop-filter: blur(20px) saturate(1.35);
}

.account-management__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 20px;
  border-bottom: 1px solid rgba(17, 17, 16, 0.07);
}

.account-management__header p,
.account-management__header h2 {
  margin: 0;
}

.account-management__header p {
  color: var(--ff-red);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
}

.account-management__header h2 {
  margin-top: 3px;
  color: var(--ff-black);
  font-family: 'Montserrat', sans-serif;
  font-size: 1.2rem;
  font-weight: 900;
  line-height: 1;
  letter-spacing: -0.045em;
  text-align: left;
  text-transform: uppercase;
}

.account-management__header > .material-icons {
  display: grid;
  width: 40px;
  height: 40px;
  place-items: center;
  color: var(--ff-red);
  border: 1px solid rgba(200, 16, 46, 0.11);
  border-radius: 13px;
  background: rgba(200, 16, 46, 0.06);
  font-size: 20px;
}

.account-action-list {
  display: grid;
  gap: 9px;
  padding: 12px;
}

.account-action {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) 24px;
  gap: 12px;
  align-items: center;
  width: 100%;
  min-height: 66px;
  margin: 0;
  padding: 11px 13px;
  color: var(--ff-black);
  border: 1px solid rgba(17, 17, 16, 0.08);
  border-radius: 17px;
  background: rgba(255, 255, 255, 0.72);
  font: inherit;
  text-align: left;
  cursor: pointer;
  appearance: none;
  transition: transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease;
}

.account-action:hover:not(:disabled) {
  border-color: rgba(200, 16, 46, 0.18);
  box-shadow: 0 10px 24px rgba(47, 38, 32, 0.08);
  transform: translateY(-1px);
}

.account-action:disabled {
  opacity: 0.6;
  cursor: wait;
}

.account-action > .material-icons:last-child {
  justify-self: end;
  color: var(--ff-muted);
  font-size: 19px;
}

.account-action__icon {
  display: grid;
  width: 42px;
  height: 42px;
  place-items: center;
  color: var(--ff-red);
  border: 1px solid rgba(200, 16, 46, 0.11);
  border-radius: 13px;
  background: rgba(200, 16, 46, 0.06);
  font-size: 20px;
}

.account-action__copy {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}

.account-action__copy strong {
  font-size: 0.86rem;
  line-height: 1.2;
}

.account-action__copy small {
  overflow: hidden;
  color: var(--ff-muted);
  font-size: 0.6875rem;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.account-action--danger {
  color: var(--ff-red-dark);
  border-color: rgba(200, 16, 46, 0.1);
  background: rgba(200, 16, 46, 0.045);
}

.account-action--danger .account-action__icon {
  color: var(--ff-white);
  border-color: transparent;
  background: var(--ff-red);
}

.account-panel {
  margin: -2px 4px 2px;
  border: 1px solid rgba(17, 17, 16, 0.08);
  border-radius: 16px;
  background: rgba(245, 244, 240, 0.75);
}

.account-contacts {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1px;
  overflow: hidden;
  background: rgba(17, 17, 16, 0.07);
}

.account-contacts a {
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr) 17px;
  gap: 8px;
  align-items: center;
  min-width: 0;
  padding: 12px;
  color: var(--ff-black);
  background: rgba(255, 255, 255, 0.84);
  text-decoration: none;
}

.account-contacts a > .material-icons:first-child {
  display: grid;
  width: 30px;
  height: 30px;
  place-items: center;
  color: var(--ff-red);
  border-radius: 10px;
  background: rgba(200, 16, 46, 0.07);
  font-size: 16px;
}

.account-contacts a > .material-icons:last-child {
  color: var(--ff-muted);
  font-size: 15px;
}

.account-contacts a > span:nth-child(2) {
  display: flex;
  min-width: 0;
  flex-direction: column;
}

.account-contacts small {
  color: var(--ff-muted);
  font-size: 0.6875rem;
}

.account-contacts strong {
  overflow: hidden;
  font-size: 0.7rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.deletion-panel {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr);
  gap: 10px;
  padding: 14px;
  color: var(--ff-muted);
}

.deletion-panel > .material-icons {
  color: var(--ff-red);
  font-size: 21px;
}

.deletion-panel strong {
  color: var(--ff-black);
  font-size: 0.78rem;
}

.deletion-panel p {
  margin: 4px 0 12px;
  font-size: 0.7rem;
  line-height: 1.45;
}

.deletion-panel__button {
  display: inline-flex;
  min-height: 40px;
  align-items: center;
  gap: 7px;
  padding: 9px 13px;
  color: var(--ff-white);
  border-radius: 999px;
  background: var(--ff-red);
  font-size: 0.6875rem;
  font-weight: 700;
  text-decoration: none;
}

.deletion-panel__button .material-icons {
  font-size: 16px;
}

.account-action-error {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 0;
  padding: 10px 12px;
  color: var(--ff-red-dark);
  border: 1px solid rgba(200, 16, 46, 0.12);
  border-radius: 13px;
  background: rgba(200, 16, 46, 0.06);
  font-size: 0.72rem;
  font-weight: 700;
}

.account-action-error .material-icons {
  font-size: 17px;
}

.account-page :is(input, select, button, a):focus-visible {
  outline: 3px solid rgba(200, 16, 46, 0.28);
  outline-offset: 3px;
}

@media (max-width: 680px) {
  .account-shell {
    gap: 14px;
    padding: 28px 12px 138px;
  }

  .account-summary {
    grid-template-columns: 52px minmax(0, 1fr) auto;
    gap: 12px;
    margin-top: -8px;
    padding: 15px;
    border-radius: 20px;
  }

  .account-summary__avatar {
    width: 52px;
    height: 52px;
    border-radius: 16px;
    font-size: 0.98rem;
  }

  .account-summary__status {
    padding: 7px 8px;
    font-size: 0;
  }

  .account-summary__status > span {
    width: 8px;
    height: 8px;
  }

  .profile-card {
    border-radius: 22px;
  }

  .profile-card__header {
    grid-template-columns: 46px minmax(0, 1fr) auto;
    gap: 11px;
    padding: 17px 15px;
  }

  .profile-card__icon {
    width: 46px;
    height: 46px;
    border-radius: 14px;
  }

  .profile-card__header h2 {
    font-size: 1.02rem;
  }

  .profile-card__badge {
    padding: 6px 8px;
    font-size: 0.6875rem;
  }

  .form-grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 17px;
    padding: 19px 15px 16px;
  }

  .field.full,
  .form-actions.full {
    grid-column: auto;
  }

  .form-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .form-actions__copy {
    max-width: none;
  }

  .save-button {
    width: 100%;
  }

  .account-note {
    margin: 0 7px;
  }

  .account-management {
    margin: 0 7px;
  }

  .account-management__header {
    padding: 16px;
  }

  .account-contacts {
    grid-template-columns: minmax(0, 1fr);
  }

  .account-contacts a {
    padding: 10px 12px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .loading-card__spinner {
    animation: none;
  }

  .field input,
  .birthdate-group select,
  .save-button,
  .account-action {
    transition: none;
  }
}
</style>
